/**
 * DropboxRevokeCard — owner-selected scope, background job + polling.
 *
 * The revoke runs as a background task on the server; this card starts
 * the job and polls its status every 2s, rendering live progress
 * (revoked / failed / already_done / skipped counts + current page) and
 * the growing per-link result table.
 *
 * Rules baked into the UX (matching the server contract):
 *   - Nothing is pre-selected.
 *   - The env `DROPBOX_UPLOAD_FOLDER` is shown as context only.
 *   - Confirm phrase must exactly equal `REVOKE ` + sorted normalised
 *     folder names.
 *   - Folder navigation links inside a targeted folder are always skipped.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertOctagon, Link2Off, ListChecks, Loader2, ShieldAlert, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;

function authHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normaliseFolder(name) {
  const n = (name || '').trim().toLowerCase();
  if (!n) return '/';
  const withSlash = n.startsWith('/') ? n : '/' + n;
  const trimmed = withSlash.replace(/\/+$/, '');
  return trimmed || '/';
}

function expectedPhrase(selectedFolders) {
  const norm = Array.from(new Set(selectedFolders.map(normaliseFolder))).filter(Boolean).sort();
  if (norm.length === 0) return '';
  return `REVOKE ${norm.join(' ')}`;
}

export default function DropboxRevokeCard() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const requiredPhrase = useMemo(
    () => expectedPhrase(selectedFolders),
    [selectedFolders],
  );

  const totalSelectedFiles = useMemo(() => {
    if (!preview?.top_folders) return 0;
    const set = new Set(selectedFolders.map(normaliseFolder));
    return preview.top_folders
      .filter((row) => set.has(row.folder))
      .reduce((acc, row) => acc + (row.file_count || 0), 0);
  }, [preview, selectedFolders]);

  const totalPreservedFiles = useMemo(() => {
    if (!preview?.top_folders) return 0;
    const set = new Set(selectedFolders.map(normaliseFolder));
    return preview.top_folders
      .filter((row) => !set.has(row.folder))
      .reduce((acc, row) => acc + (row.file_count || 0), 0);
  }, [preview, selectedFolders]);

  const fetchPreview = useCallback(async () => {
    setLoadingPreview(true);
    setSelectedFolders([]);
    setConfirm('');
    setJob(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/dropbox/shared-links/preview`,
        { headers: authHeaders() },
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(`Preview failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const toggleFolder = useCallback((folder) => {
    setSelectedFolders((cur) => {
      const norm = normaliseFolder(folder);
      return cur.includes(norm) ? cur.filter((f) => f !== norm) : [...cur, norm];
    });
    setConfirm('');
  }, []);

  // Poll the job status endpoint while a job is in-flight.
  useEffect(() => {
    if (!job?.job_id) return undefined;
    if (job.status === 'completed' || job.status === 'failed') return undefined;

    const tick = async () => {
      try {
        const res = await axios.get(
          `${API}/api/admin/dropbox/shared-links/jobs/${job.job_id}`,
          { headers: authHeaders() },
        );
        setJob(res.data);
      } catch (e) {
        // Non-fatal — poll keeps trying; hard 404/401 breaks the loop.
        if (e?.response?.status === 404 || e?.response?.status === 401) {
          toast.error(`Job polling failed: ${e?.response?.data?.detail || e.message}`);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [job?.job_id, job?.status]);

  const startJob = useCallback(async () => {
    if (selectedFolders.length === 0) {
      toast.error('Check at least one top-level folder to target.');
      return;
    }
    if (confirm !== requiredPhrase) {
      toast.error('Typed phrase does not match the required confirmation.');
      return;
    }
    setStarting(true);
    setJob(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/dropbox/shared-links/revoke`,
        { folders: selectedFolders, confirm },
        { headers: authHeaders() },
      );
      setJob({ ...res.data, progress: {}, results: [] });
      toast.success(`Job ${res.data.job_id} started`);
      setConfirm('');
    } catch (e) {
      toast.error(`Failed to start job: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setStarting(false);
    }
  }, [selectedFolders, confirm, requiredPhrase]);

  const p = job?.progress || {};
  const isRunning = job && (job.status === 'pending' || job.status === 'running');
  const isDone = job && (job.status === 'completed' || job.status === 'failed');
  const denom = p.total_links_seen || 0;
  const processed = p.processed || 0;
  const pct = denom > 0 ? Math.min(100, Math.floor((processed / denom) * 100)) : 0;

  return (
    <Card className="mb-4 border-rose-300" data-testid="dropbox-revoke-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-rose-700" />
          <CardTitle className="text-lg">Revoke Dropbox Shared Links</CardTitle>
        </div>
        <CardDescription>
          Runs as a background job on the server so Cloudflare&apos;s request
          timeout can&apos;t cut it short. This card polls the job status every
          2 s and streams the per-link results as they land.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Config visibility row — CONTEXT ONLY */}
        {preview?.dropbox_upload_folder_setting !== undefined && (
          <div className="flex items-start gap-2 text-xs border rounded p-2 bg-amber-50/40"
               data-testid="dropbox-revoke-env-context">
            <Info className="h-3.5 w-3.5 mt-0.5 text-amber-700 flex-shrink-0" />
            <div>
              <span className="font-semibold text-amber-900">Where the app currently writes: </span>
              <code className="font-mono text-amber-900" data-testid="dropbox-revoke-env-value">
                {preview.dropbox_upload_folder_setting}
              </code>
              <span className="text-slate-600 ml-2">
                (from <code>DROPBOX_UPLOAD_FOLDER</code> — shown for context only;
                does not drive scope)
              </span>
            </div>
          </div>
        )}

        {/* Step 1 — Preview */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={fetchPreview}
              disabled={loadingPreview || isRunning}
              data-testid="dropbox-revoke-preview-btn"
            >
              {loadingPreview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListChecks className="h-4 w-4 mr-2" />}
              Preview top folders
            </Button>
            {preview && (
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="secondary" data-testid="dropbox-revoke-top-folder-count">
                  {preview.totals.distinct_top_folders} top-level folder(s)
                </Badge>
                <Badge variant="outline">
                  {preview.totals.all_sampled} link(s) sampled
                </Badge>
                {preview.totals.truncated && (
                  <Badge variant="outline" className="bg-amber-50">
                    truncated after {preview.totals.pages_scanned} page(s)
                  </Badge>
                )}
              </div>
            )}
          </div>

          {preview?.top_folders && (
            <div className="border rounded overflow-hidden" data-testid="dropbox-revoke-folder-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 text-left w-12">Target</th>
                    <th className="p-2 text-left">Top-level folder</th>
                    <th className="p-2 text-right">File links</th>
                    <th className="p-2 text-right">Folder links</th>
                    <th className="p-2 text-left">Sample paths</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.top_folders.map((row) => {
                    const isChecked = selectedFolders.includes(row.folder);
                    const safeId = row.folder.replace(/[^\w-]/g, '_');
                    return (
                      <tr key={row.folder} className={isChecked ? 'bg-rose-50' : ''}
                          data-testid={`dropbox-revoke-folder-row-${safeId}`}>
                        <td className="p-2">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggleFolder(row.folder)}
                            disabled={isRunning}
                            data-testid={`dropbox-revoke-folder-checkbox-${safeId}`}
                          />
                        </td>
                        <td className="p-2 font-mono">{row.folder}</td>
                        <td className="p-2 text-right font-mono">{row.file_count}</td>
                        <td className="p-2 text-right font-mono text-slate-500">
                          {row.folder_link_count}
                        </td>
                        <td className="p-2 text-slate-500 truncate max-w-md">
                          {(row.sample_paths || []).map((s) => (
                            <div key={s} className="truncate font-mono">{s}</div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                  {preview.top_folders.length === 0 && (
                    <tr><td colSpan={5} className="p-3 text-slate-500 italic text-center">
                      No shared links found on this Dropbox account.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {preview && selectedFolders.length > 0 && !isRunning && (
            <div className="text-xs flex items-center gap-2 flex-wrap" data-testid="dropbox-revoke-live-impact">
              <Badge className="bg-rose-100 text-rose-900 border-rose-300">
                Will revoke: ~{totalSelectedFiles} file link(s) across {selectedFolders.length} folder(s)
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300">
                Will preserve: {totalPreservedFiles} file link(s) in unchecked folders
              </Badge>
            </div>
          )}

          {preview?.known_limits?.length ? (
            <div className="text-xs text-slate-600 border-l-2 border-amber-400 pl-3">
              <div className="font-medium text-amber-900 mb-1">Known limits:</div>
              <ul className="list-disc ml-4 space-y-0.5">
                {preview.known_limits.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          ) : null}
        </div>

        {/* Step 2 — Confirm phrase + start job */}
        <div className="border-t pt-3 space-y-2">
          <div className="text-sm text-slate-700">
            {selectedFolders.length === 0 ? (
              <span className="text-slate-500 italic">
                Check one or more folders above to unlock the confirmation phrase.
              </span>
            ) : (
              <>
                Type this phrase verbatim to start the background revoke job:{' '}
                <code className="font-mono text-rose-700 font-semibold"
                      data-testid="dropbox-revoke-required-phrase">
                  {requiredPhrase}
                </code>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder={requiredPhrase || 'REVOKE /folder-name'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="max-w-xl font-mono"
              disabled={selectedFolders.length === 0 || isRunning}
              data-testid="dropbox-revoke-confirm-input"
            />
            <Button
              variant="destructive"
              onClick={startJob}
              disabled={
                starting || isRunning ||
                selectedFolders.length === 0 ||
                confirm !== requiredPhrase
              }
              data-testid="dropbox-revoke-run-btn"
            >
              {starting || isRunning
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Link2Off className="h-4 w-4 mr-2" />}
              {isRunning ? 'Running…' : 'Start revoke job'}
            </Button>
          </div>
        </div>

        {/* Live job progress + results */}
        {job && (
          <div className="border-t pt-3 space-y-2" data-testid="dropbox-revoke-job">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="text-slate-500">Job:</span>
              <code className="text-xs font-mono" data-testid="dropbox-revoke-job-id">{job.job_id}</code>
              <Badge
                className={
                  job.status === 'completed' ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
                  job.status === 'failed' ? 'bg-rose-100 text-rose-900 border-rose-300' :
                  'bg-sky-100 text-sky-900 border-sky-300'
                }
                data-testid="dropbox-revoke-job-status"
              >
                {job.status}
              </Badge>
              {p.listing_done && (
                <Badge variant="outline" data-testid="dropbox-revoke-job-pages">
                  pages scanned: {p.pages_scanned}
                </Badge>
              )}
              {!p.listing_done && isRunning && (
                <Badge variant="outline">
                  listing… page {p.pages_scanned || 0}
                </Badge>
              )}
            </div>

            {/* Progress bar */}
            {(p.total_links_seen || 0) > 0 && (
              <div className="space-y-1" data-testid="dropbox-revoke-job-progress">
                <div className="h-2 w-full rounded bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${job.status === 'completed' ? 'bg-emerald-500' : job.status === 'failed' ? 'bg-rose-500' : 'bg-sky-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-xs text-slate-600 font-mono">
                  {processed} / {denom} link(s) processed ({pct}%)
                </div>
              </div>
            )}

            {/* Counter badges */}
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300"
                     data-testid="dropbox-revoke-counter-revoked">
                Revoked: {p.revoked ?? 0}
              </Badge>
              <Badge className="bg-slate-100 text-slate-700 border-slate-300"
                     data-testid="dropbox-revoke-counter-already">
                Already gone: {p.already_done ?? 0}
              </Badge>
              <Badge className="bg-rose-100 text-rose-900 border-rose-300"
                     data-testid="dropbox-revoke-counter-failed">
                Failed: {p.failed ?? 0}
              </Badge>
              <Badge variant="outline" data-testid="dropbox-revoke-counter-folder">
                Skipped folder-nav: {p.skipped_folder ?? 0}
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300"
                     data-testid="dropbox-revoke-counter-oos">
                Preserved out-of-scope: {p.skipped_out_of_scope ?? 0}
              </Badge>
              {p.truncated_before_end_of_account && (
                <Badge variant="outline" className="bg-amber-50">
                  truncated — re-run to sweep next batch
                </Badge>
              )}
            </div>

            {job.error && (
              <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">
                Error: {job.error}
              </div>
            )}

            {/* Results table */}
            {(job.results || []).length > 0 && (
              <div className="max-h-72 overflow-auto border rounded text-xs" data-testid="dropbox-revoke-result">
                <table className="w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-1">Status</th>
                      <th className="text-left p-1">Path</th>
                      <th className="text-left p-1">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(job.results || []).slice(-300).map((r, i) => (
                      <tr key={i} className={r.status === 'failed' ? 'bg-rose-50' : ''}>
                        <td className="p-1 font-mono">{r.status}</td>
                        <td className="p-1 truncate max-w-md font-mono">{r.path || r.name}</td>
                        <td className="p-1 text-rose-800">{r.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(job.results || []).length > 300 && (
                  <div className="p-2 text-slate-500">
                    showing latest 300 rows ({(job.results || []).length} total — full list in server log)
                  </div>
                )}
              </div>
            )}

            {isDone && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchPreview}
                        data-testid="dropbox-revoke-refresh-preview-btn">
                  Refresh preview (verify live reality)
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setJob(null)}>
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Docs footer */}
        <div className="text-xs text-slate-500 border-t pt-2 flex items-start gap-2">
          <AlertOctagon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <div>
            Run this AFTER migration <code>0055_backfill_raw_dropbox_urls</code>{' '}
            has rewritten the URLs stored in Mongo. Re-runs are idempotent —
            links Dropbox already dropped are counted as <em>already_done</em>,
            not <em>failed</em>. If the account has more links than a single
            job scans, the badge tells you to re-run.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
